import type { ReactNode } from "react"
import { useEffect, useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
	IconPlusOutline18 as Plus,
	IconTriangleWarningOutline18 as WarningIcon,
} from "nucleo-ui-outline-18"

import {
	liveSlackConnectionIds,
	pro,
	resolveSlackWorkspaceLimit,
	scale,
	SLACK_WORKSPACE_LIMIT_REASON,
} from "@productlane/lib"
import {
	Alert,
	AlertDialog,
	Badge,
	Button,
	IntegrationTemplate,
	Loader,
} from "@productlane/ui"
import { SlackIcon } from "@productlane/ui/src/icons/slack-logo"

import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { useSlackWorkspaceOptions } from "@/lib/slack-workspaces"
import { useSlackConnections, useZeroWorkspace } from "@/lib/zero"
import { PlanPill } from "../plan-pill"
import { HowItWorksButton } from "../settings-switches/how-it-works-button"
import { SettingsContainer } from "../settings-switches/settings-container"
import { IntegrationLayout } from "./integration-layout"
import { slackPausedNotice, slackWorkspaceLimitToast } from "./slack-plan-copy"
import {
	connectSlackWorkspace,
	SlackWorkspaceCard,
} from "./slack-workspace-card"

const SLACK_CONNECT_LONG_DESCRIPTION = (
	<div>
		<div className="mb-8">
			<div className="mb-2 text-lg font-semibold text-primary">
				How the Slack Integration Works
			</div>
			<div className="mb-6 text-sm leading-relaxed text-secondary">
				The Slack integration turns your Slack Connect channels into a powerful
				customer support platform. Invite the Productlane bot to every customer
				Slack Connect channel to automatically sync all conversations to
				Productlane, where you can manage support requests, link them to Linear
				issues, and keep your team organized.
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Customer Support in Slack
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Slack Connect Channels:</strong>{" "}
						Add the bot to customer Slack Connect channels for support.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Automatic Sync:</strong> All
						messages and threads sync to Productlane automatically.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">2-Way Communication:</strong> Reply
						from Productlane and it appears in Slack, and vice versa.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Link to Linear:</strong> Connect
						support threads directly to Linear issues.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Setup Instructions
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">1.</span>
					<span>
						Connect Productlane to your Slack workspace by clicking
						&quot;Connect&quot; below.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">2.</span>
					<span>
						Invite the Productlane bot to each customer Slack Connect channel.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">3.</span>
					<span>
						Messages will automatically create threads in Productlane.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">4.</span>
					<span>
						Reply to customers from Productlane or Slack - both stay in sync.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Additional Features
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Push to Productlane:</strong>{" "}
						Right-click any message to push it as a thread.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Notifications:</strong> Configure a
						channel to receive notifications for new threads.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Team Collaboration:</strong> Your
						entire team can manage support from Productlane.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-6">
			<div className="mb-2 text-base font-semibold text-primary">
				Best Practices
			</div>
			<div className="text-sm leading-relaxed text-secondary">
				For the best experience, invite the Productlane bot to all your
				customer-facing Slack Connect channels. This ensures every customer
				conversation is captured in Productlane, giving you a centralized view
				of all support requests and making it easy to track customer issues.
			</div>
		</div>
	</div>
)

export function SlackIntegrationPage() {
	const { mutateAsync, isLoading } = api.workspace.disconnectSlack.useMutation()

	const slackQuery = api.workspace.slackSettings.useQuery(undefined, {
		staleTime: 30_000,
	})
	const connections = useSlackConnections()

	const { passes: hasProPlan } = useRequiredPlans(pro.andUp)
	const planQuery = api.workspace.activePlan.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	const plan = planQuery.data
	const workspaceId = useZeroWorkspace()?.id
	const slackLimit = plan ? resolveSlackWorkspaceLimit(plan, workspaceId) : 0
	const canAddWorkspace = connections.length < slackLimit
	const liveIds = plan
		? liveSlackConnectionIds(connections, plan, workspaceId)
		: null
	const workspaceOptions = useSlackWorkspaceOptions()
	const pausedNotice =
		plan && liveIds
			? slackPausedNotice({
					plan,
					pausedCount: connections.length - liveIds.size,
					liveName:
						workspaceOptions.find((option) => liveIds.has(option.id))?.name ??
						null,
				})
			: null

	const navigate = useNavigate()
	const limitReached =
		useSearch({ strict: false, select: (s) => s.reason }) ===
		SLACK_WORKSPACE_LIMIT_REASON
	useEffect(() => {
		if (!limitReached || !plan) return
		const copy = slackWorkspaceLimitToast(plan)
		toast.info({
			title: copy.title,
			description: copy.description,
			icon: SlackIcon,
			...(copy.canUpgrade && {
				action: {
					label: "View plans",
					callback: () => void navigate({ to: "/settings/plans" }),
				},
			}),
		})
		void navigate({ to: "/settings/integrations/slack", replace: true })
	}, [limitReached, plan, navigate])

	const [dialogOpen, setDialogOpen] = useState(false)

	const isSlackConnected =
		slackQuery.data?.connected && slackQuery.data.isNewIntegration

	const isOldSlackConnected =
		slackQuery.data?.connected && !slackQuery.data.isNewIntegration

	const disconnectSlack = async () => {
		analytics.track("disconnect-slack")
		await mutateAsync()
		await slackQuery.refetch()
		setDialogOpen(false)
	}

	if (isOldSlackConnected)
		return (
			<>
				<Alert header="Slack Integration Update Required" variant="warning">
					<div>
						Your current Slack integration is no longer supported. Please
						disconnect and reconnect Slack to continue using this feature.
					</div>
					<Button
						onClick={() => setDialogOpen(true)}
						isLoading={isLoading}
						className="mt-2"
					>
						Disconnect
					</Button>
				</Alert>
				<AlertDialog
					variant="positive"
					isOpen={dialogOpen}
					onOpenChange={setDialogOpen}
					confirmText="Disconnect"
					title="Disconnect Slack Integration?"
					description="Are you sure you want to disconnect Slack? Your data will remain saved, but the integration will be disabled."
					onCancel={() => {
						setDialogOpen(false)
					}}
					onConfirm={disconnectSlack}
				/>
			</>
		)

	return (
		<>
			<MetaInfo title="Slack Integration" />
			<IntegrationLayout />
			<IntegrationTemplate
				logo={<SlackIcon className="w-10" />}
				name={
					<div className="flex flex-wrap items-center gap-2">
						Slack
						{isSlackConnected ? (
							<Badge
								variant="green"
								size="sm"
								className="!text-green-700 dark:!text-green-400"
							>
								<span className="size-1.5 rounded-full bg-green-500" />
								Connected
							</Badge>
						) : (
							<PlanPill plan="pro" />
						)}
					</div>
				}
				description="Sync conversations from Slack."
				notice={
					slackQuery.data?.broadcastStopped ? (
						<SlackNotice>
							New threads aren&apos;t posting to Slack. Reconnect, then choose
							your notification channel under Options.
						</SlackNotice>
					) : pausedNotice ? (
						<SlackNotice>{pausedNotice}</SlackNotice>
					) : null
				}
				button={
					<div className="flex items-center gap-2">
						<HowItWorksButton
							article="eba421f3-5a25-48cc-88a3-2658544b24fa"
							variant="icon"
						/>
						{hasProPlan || slackLimit > 0 ? (
							<RoleCheck not={["VIEWER"]}>
								{isSlackConnected ? (
									canAddWorkspace ? (
										<Button
											variant="outline"
											Icon={Plus}
											onClick={connectSlackWorkspace}
										>
											Connect workspace
										</Button>
									) : (
										<UpgradeButton required={scale.andUp} />
									)
								) : (
									<Button onClick={connectSlackWorkspace}>Connect</Button>
								)}
							</RoleCheck>
						) : (
							<UpgradeButton required={pro.andUp} />
						)}
					</div>
				}
				instructions={
					!isSlackConnected ? (
						SLACK_CONNECT_LONG_DESCRIPTION
					) : slackQuery.data ? (
						<>
							<div className="space-y-4">
								<div className="text-sm font-medium">Workspaces</div>
								{connections.map((settings) => (
									<SlackWorkspaceCard
										key={settings.id}
										settings={settings}
										paused={!!liveIds && !liveIds.has(settings.id)}
										status={slackQuery.data.connections.find(
											(status) => status.id === settings.id,
										)}
									/>
								))}
							</div>
							<PersonalSlackNotificationsCard />
						</>
					) : (
						<div className="flex justify-center">
							<Loader />
						</div>
					)
				}
			/>
		</>
	)
}

function SlackNotice({ children }: { children: ReactNode }) {
	return (
		<div className="-mx-5 -mb-5 flex items-center gap-2 border-t border-gray-200 px-5 py-3 text-[13px] text-amber-700 dark:border-gray-700/80 dark:text-amber-400">
			<WarningIcon className="h-4 w-4 flex-none" />
			<span>{children}</span>
		</div>
	)
}

function PersonalSlackNotificationsCard() {
	const navigate = useNavigate()
	return (
		<SettingsContainer className="mt-4">
			<div className="space-y-4">
				<div className="text-sm">
					<div className="flex items-center justify-between">
						<div className="cursor-default">
							<p className="font-medium">Personal Slack notifications</p>
							<p className="mt-1 text-secondary">
								Set up personal Slack notifications for new assignments or
								comment mentions.
							</p>
						</div>
						<Button
							variant="default"
							onClick={() => void navigate({ to: "/settings/notifications" })}
						>
							Set up
						</Button>
					</div>
				</div>
			</div>
		</SettingsContainer>
	)
}
